import React, { useState, Component } from 'react';
import { SEO } from '../components/SEO';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArrowRightIcon, SearchIcon, ZapIcon, FileTextIcon, MapPinIcon, TrendingUpIcon, ImageIcon, BrainIcon, GlobeIcon, CheckCircleIcon, BarChartIcon, LinkIcon, CodeIcon, SettingsIcon, LayersIcon, MousePointerClickIcon, DatabaseIcon, WrenchIcon, RocketIcon, RefreshCwIcon, TargetIcon, ChevronDownIcon, ChevronUpIcon, ShieldCheckIcon, Share2Icon, LayoutTemplateIcon, NetworkIcon, SmartphoneIcon, MessageSquareIcon, PlayCircleIcon, AlertTriangleIcon, ChevronRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export const SEOPage: React.FC = () => {
  const [expandedComponent, setExpandedComponent] = useState<string | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  // --- DATA STRUCTURES ---
  const seoFactors = [{
    icon: <WrenchIcon className="w-6 h-6" data-id="element-1622" />,
    title: 'Techniek',
    desc: 'De fundering. Als Google je site niet kan lezen, houdt het op.'
  }, {
    icon: <FileTextIcon className="w-6 h-6" data-id="element-1623" />,
    title: 'Content',
    desc: 'Het antwoord op de vraag van je klant. Relevant en diepgaand.'
  }, {
    icon: <ShieldCheckIcon className="w-6 h-6" data-id="element-1624" />,
    title: 'Autoriteit',
    desc: 'Vertrouwen. Andere sites die naar jou verwijzen als expert.'
  }, {
    icon: <MousePointerClickIcon className="w-6 h-6" data-id="element-1625" />,
    title: 'UX',
    desc: 'Gebruiksvriendelijkheid. Een bezoeker moet makkelijk navigeren.'
  }, {
    icon: <Share2Icon className="w-6 h-6" data-id="element-1626" />,
    title: 'Distributie',
    desc: 'Hoe je content verspreid wordt over verschillende kanalen.'
  }];
  const maturityPhases = [{
    phase: 'Basis',
    levels: '0-2',
    title: 'De Fundering',
    happening: 'Website staat live, maar trekt nauwelijks relevant verkeer. Losse blogs zonder strategie.',
    missing: 'Technische basis, logische structuur en gerichte zoekwoorden.',
    color: 'bg-gray-100 border-gray-200 text-gray-800'
  }, {
    phase: 'Groei',
    levels: '3-5',
    title: 'De Structuur',
    happening: "Verkeer neemt toe. Pagina's ranken op specifieke termen. Leads beginnen binnen te komen.",
    missing: 'Silo-structuur, interne linking en schaalbare content.',
    color: 'bg-blue-50 border-blue-200 text-[#007AFF]'
  }, {
    phase: 'Dominantie',
    levels: '6-9',
    title: 'Het Systeem',
    happening: 'Je bent de autoriteit in jouw niche. Verkeer is voorspelbaar en schaalbaar. Conversies zijn hoog.',
    missing: 'Niets. Het is nu een kwestie van uitbouwen en domineren.',
    color: 'bg-[#1B2B44] border-[#1B2B44] text-white'
  }];
  const seoComponents = [{
    id: 'technisch',
    title: 'Technische SEO',
    icon: <CodeIcon className="w-5 h-5" data-id="element-1627" />,
    explanation: 'De motor van je website. Zonder goede techniek kan Google je content niet vinden of begrijpen.',
    happens: 'We optimaliseren laadtijden (Core Web Vitals), fixen dode links, verbeteren de mobiele weergave en zorgen voor een perfecte indexatie.',
    matters: 'Als de techniek faalt, is alle andere moeite voor niets. Een snelle, foutloze site is een harde eis van Google.',
    blogLink: '/seo/blog/technische-seo-uitleg',
    blogTitle: 'Wat is technische SEO?'
  }, {
    id: 'onpage',
    title: 'On-page SEO',
    icon: <LayoutTemplateIcon className="w-5 h-5" data-id="element-1628" />,
    explanation: "Het optimaliseren van individuele pagina's zodat ze perfect aansluiten bij wat de zoeker wil zien.",
    happens: 'We structureren titels (H1, H2), optimaliseren meta-data, verbeteren de leesbaarheid en zorgen voor de juiste zoekintentie.',
    matters: 'Dit vertelt Google exact waar de pagina over gaat en overtuigt de bezoeker om te klikken in de zoekresultaten.',
    blogLink: '/seo/blog/hoe-optimaliseer-je-een-pagina',
    blogTitle: 'Hoe optimaliseer je een pagina voor Google?'
  }, {
    id: 'architectuur',
    title: 'Site Architectuur',
    icon: <NetworkIcon className="w-5 h-5" data-id="element-1629" />,
    explanation: "De manier waarop je website is opgebouwd en hoe pagina's met elkaar verbonden zijn.",
    happens: 'We bouwen een logische hiërarchie (Silo structuur) en implementeren een slimme interne linkstrategie.',
    matters: 'Een goede structuur verdeelt de "SEO-waarde" over je hele site en helpt bezoekers (en Google) de weg te vinden.',
    blogLink: '/seo/blog/waarom-interne-linking-cruciaal-is',
    blogTitle: 'Waarom interne linking cruciaal is'
  }, {
    id: 'content',
    title: 'Content Strategie',
    icon: <FileTextIcon className="w-5 h-5" data-id="element-1630" />,
    explanation: 'Het plan achter wat je schrijft. Geen losse flodders, maar een doordacht systeem van artikelen.',
    happens: 'We creëren topic clusters: één hoofdpagina (pillar) met daaromheen ondersteunende artikelen die allemaal naar elkaar linken.',
    matters: 'Dit bouwt topical authority op. Je laat zien dat je dé expert bent over een heel onderwerp, niet slechts één zoekwoord.',
    blogLink: '/seo/blog/zoekintentie-uitleg',
    blogTitle: 'Wat is zoekintentie?'
  }, {
    id: 'autoriteit',
    title: 'Autoriteit (Off-page)',
    icon: <LinkIcon className="w-5 h-5" data-id="element-1631" />,
    explanation: 'Hoe de rest van het internet naar jouw website kijkt. Ben je een betrouwbare bron?',
    happens: 'We verzamelen kwalitatieve links (backlinks) van andere relevante, betrouwbare websites naar jouw site.',
    matters: 'Backlinks werken als "stemmen". Hoe meer kwalitatieve stemmen, hoe hoger Google je waardeert.',
    blogLink: '/seo/blog/hoe-bouw-je-autoriteit-op',
    blogTitle: 'Hoe bouw je autoriteit op?'
  }, {
    id: 'lokaal',
    title: 'Local SEO',
    icon: <MapPinIcon className="w-5 h-5" data-id="element-1632" />,
    explanation: 'Gevonden worden door mensen in jouw directe omgeving die nu een oplossing zoeken.',
    happens: 'We optimaliseren je Google Bedrijfsprofiel, Apple Business Connect en zorgen voor lokale vermeldingen.',
    matters: 'Voor veel adviseurs komen de beste leads uit de eigen regio. Local SEO zorgt dat jij bovenaan staat op de kaart.',
    blogLink: '/seo/blog/local-seo-uitleg',
    blogTitle: 'Wat is local SEO en hoe word je lokaal vindbaar?'
  }, {
    id: 'ux',
    title: 'UX & Conversie',
    icon: <MousePointerClickIcon className="w-5 h-5" data-id="element-1633" />,
    explanation: 'De gebruikerservaring. Wat doet een bezoeker zodra hij op je site landt?',
    happens: 'We analyseren gedrag, verbeteren de navigatie, versnellen het proces en plaatsen duidelijke call-to-actions.',
    matters: 'Verkeer zonder conversie is waardeloos. Een goede UX houdt bezoekers langer vast (goed voor SEO) en levert leads op.',
    blogLink: '/seo/blog/wat-is-ux-en-waarom-beinvloedt-het-seo',
    blogTitle: 'Wat is UX en waarom beïnvloedt het SEO?'
  }, {
    id: 'ai',
    title: 'Entity & AI SEO',
    icon: <BrainIcon className="w-5 h-5" data-id="element-1634" />,
    explanation: 'Optimaliseren voor hoe moderne (AI) zoekmachines de wereld begrijpen in concepten, niet alleen in woorden.',
    happens: 'We gebruiken structured data (Schema markup) om entiteiten (personen, bedrijven, diensten) keihard te definiëren voor Google.',
    matters: 'Zoekmachines worden slimmer. Ze zoeken naar de betekenis achter woorden. Wie dit goed doet, domineert de toekomst.',
    blogLink: '/seo/blog/wat-is-entity-seo',
    blogTitle: 'Wat is entity SEO?'
  }, {
    id: 'distributie',
    title: 'Distributie',
    icon: <Share2Icon className="w-5 h-5" data-id="element-1635" />,
    explanation: 'Hoe je content bij de juiste mensen krijgt, buiten Google om.',
    happens: 'We zorgen dat content makkelijk deelbaar is en hergebruikt kan worden voor nieuwsbrieven en social media.',
    matters: 'SEO is traag, distributie is snel. Samen zorgen ze voor een constante stroom van bezoekers.',
    blogLink: '/seo/blog/seo-vs-advertenties',
    blogTitle: 'SEO vs advertenties: wat is het verschil?'
  }, {
    id: 'visueel',
    title: 'Visual & Media SEO',
    icon: <ImageIcon className="w-5 h-5" data-id="element-1636" />,
    explanation: "Het vindbaar maken van je afbeeldingen, video's en documenten.",
    happens: "We optimaliseren bestandsnamen, alt-teksten, compressie en video-schema's.",
    matters: "Mensen zoeken steeds vaker visueel. Bovendien maken geoptimaliseerde media je gewone pagina's sterker.",
    blogLink: '/seo/blog/wat-is-technische-seo',
    blogTitle: 'Wat is technische SEO?'
  }, {
    id: 'data',
    title: 'Data & Optimalisatie',
    icon: <BarChartIcon className="w-5 h-5" data-id="element-1637" />,
    explanation: 'Meten is weten. We sturen op harde data, niet op onderbuikgevoel.',
    happens: 'We monitoren rankings, klikfrequenties (CTR) en conversies via Google Search Console en Analytics.',
    matters: 'Zonder data ben je blind. Door te meten kunnen we continu bijsturen en het systeem verbeteren.',
    blogLink: '/seo/blog/hoe-lang-duurt-seo',
    blogTitle: 'Hoe lang duurt SEO voordat je resultaat ziet?'
  }, {
    id: 'trust',
    title: 'Trust & Legal',
    icon: <ShieldCheckIcon className="w-5 h-5" data-id="element-1638" />,
    explanation: 'E-E-A-T: Experience, Expertise, Authoritativeness, Trustworthiness.',
    happens: 'We voegen auteursprofielen toe, tonen keurmerken, reviews en zorgen voor transparante voorwaarden.',
    matters: 'Zeker in de financiële sector (YMYL, oftewel Your Money or Your Life) eist Google extreme betrouwbaarheid voordat ze je tonen.',
    blogLink: '/seo/blog/waarom-werkt-seo-vaak-niet',
    blogTitle: 'Waarom werkt SEO vaak niet?'
  }];
  const formulas = [{
    title: 'De Ranking Formule',
    formula: 'Ranking = Relevantie × Autoriteit × Vertrouwen'
  }, {
    title: 'De Groei Formule',
    formula: 'SEO groei = (Content × Structuur) × Tijd'
  }, {
    title: 'De Traffic Formule',
    formula: 'Traffic = Ranking × CTR'
  }, {
    title: 'De Conversie Formule',
    formula: 'Conversie = Traffic × Vertrouwen × Duidelijkheid'
  }, {
    title: 'De Dominantie Formule',
    formula: 'Dominantie = Silo × Content × Distributie × Tijd'
  }];
  const faqs = [{
    q: 'Wat is SEO precies?',
    a: 'SEO (Search Engine Optimization) is het proces van het optimaliseren van je website zodat deze beter vindbaar is in zoekmachines zoals Google. Het is geen eenmalig trucje, maar een systeem van techniek, content, autoriteit en gebruikerservaring dat samenwerkt om structureel relevante bezoekers aan te trekken.'
  }, {
    q: 'Hoe lang duurt SEO?',
    a: 'SEO is een lange-termijn strategie. Meestal zie je de eerste voorzichtige resultaten na 3 tot 6 maanden. Echte, structurele groei en dominantie in jouw markt kost vaak 6 tot 12 maanden. Dit hangt af van de concurrentie, de huidige staat van je website en de inzet die we leveren. Het is een investering die zich op de lange termijn exponentieel terugbetaalt.'
  }, {
    q: 'Waarom werkt SEO vaak niet?',
    a: 'SEO faalt meestal door een gebrek aan structuur of focus. Veel bedrijven schrijven losse blogs zonder onderlinge samenhang (geen silo-structuur), hebben een trage website, of richten zich op zoekwoorden waar hun doelgroep niet op zoekt. SEO werkt alleen als alle factoren (Techniek, Content, Autoriteit, UX) kloppen. Als één factor nul is, is het resultaat nul.'
  }, {
    q: 'Wat is een SEO silo?',
    a: 'Een SEO silo is een manier om je website logisch te structureren. In plaats van alle pagina\'s los naast elkaar te zetten, groepeer je gerelateerde content. Je hebt een hoofdpagina (bijv. "Hypotheekadvies") met daaronder subpagina\'s (bijv. "Hypotheek oversluiten", "Startershypotheek"). Deze linken strategisch naar elkaar. Dit maakt je site overzichtelijk voor bezoekers én Google begrijpt direct waar je expert in bent.'
  }, {
    q: 'Heb ik blogs nodig?',
    a: 'Ja, maar niet zomaar "blogs". Je hebt strategische content nodig die antwoord geeft op de vragen van je doelgroep. We noemen dit liever een kennisbank of topic clusters. Deze informatieve artikelen trekken mensen aan in de oriëntatiefase en bouwen jouw autoriteit op. Vervolgens leiden we deze bezoekers via interne links naar je commerciële dienstpagina\'s.'
  }, {
    q: 'Is SEO beter dan advertenties?',
    a: 'Ze dienen een ander doel. Advertenties (SEA) leveren direct verkeer op, maar zodra je stopt met betalen, stopt het verkeer. SEO duurt langer om op te bouwen, maar levert daarna structureel, "gratis" verkeer op. Voor duurzame groei is SEO essentieel. Vaak is een combinatie in het begin het sterkst: advertenties voor de korte termijn, SEO voor de lange termijn.'
  }, {
    q: 'Wat kost SEO?',
    a: 'SEO is geen standaard pakketje met een vaste prijs. De investering hangt af van jouw doelen, de huidige staat van je website en de concurrentie in jouw markt. We kijken eerst naar wat er nodig is om jouw doelen te bereiken en maken op basis daarvan een strategisch plan. Zie het niet als een kostenpost, maar als een investering in een structureel verkoopkanaal.'
  }];
  const blogClusters = [{
    title: 'SEO Fundamentals',
    articles: [{
      title: 'Wat is SEO en hoe werkt het?',
      slug: 'wat-is-seo-en-hoe-werkt-het'
    }, {
      title: 'Waarom werkt SEO vaak niet?',
      slug: 'waarom-werkt-seo-vaak-niet'
    }, {
      title: 'Hoe lang duurt SEO voordat je resultaat ziet?',
      slug: 'hoe-lang-duurt-seo'
    }, {
      title: 'SEO vs advertenties: wat is het verschil?',
      slug: 'seo-vs-advertenties'
    }]
  }, {
    title: 'SEO Silo (Core)',
    articles: [{
      title: 'Wat is een SEO silo en hoe werkt het?',
      slug: 'seo-silo-wat-is-het'
    }, {
      title: 'Hoe bouw je een SEO silo structuur?',
      slug: 'seo-silo-opzetten'
    }, {
      title: 'Waarom interne linking cruciaal is',
      slug: 'waarom-interne-linking-cruciaal-is'
    }, {
      title: 'Veelgemaakte fouten in website structuur',
      slug: 'veelgemaakte-fouten-website-structuur'
    }, {
      title: 'Hoe Google websites begrijpt (simpel uitgelegd)',
      slug: 'hoe-google-websites-begrijpt'
    }]
  }, {
    title: 'Technical SEO',
    articles: [{
      title: 'Wat is technische SEO?',
      slug: 'technische-seo-uitleg'
    }, {
      title: 'Wat zijn Core Web Vitals?',
      slug: 'wat-zijn-core-web-vitals'
    }, {
      title: 'Hoe werkt indexatie in Google?',
      slug: 'hoe-werkt-indexatie'
    }, {
      title: 'Wat doet een XML sitemap?',
      slug: 'wat-doet-een-xml-sitemap'
    }, {
      title: 'Wat zijn canonicals en waarom zijn ze belangrijk?',
      slug: 'wat-zijn-canonicals'
    }]
  }, {
    title: 'Content SEO',
    articles: [{
      title: 'Hoe schrijf je SEO teksten?',
      slug: 'hoe-schrijf-je-seo-teksten'
    }, {
      title: 'Wat is zoekintentie?',
      slug: 'zoekintentie-uitleg'
    }, {
      title: 'Hoe kies je de juiste keywords?',
      slug: 'hoe-kies-je-keywords'
    }, {
      title: 'Wat is het verschil tussen blogs en landingspagina’s?',
      slug: 'blogs-vs-landingspaginas'
    }, {
      title: 'Hoe optimaliseer je een pagina voor Google?',
      slug: 'hoe-optimaliseer-je-een-pagina'
    }]
  }, {
    title: 'Local SEO',
    articles: [{
      title: 'Wat is local SEO?',
      slug: 'local-seo-uitleg'
    }, {
      title: 'Hoe werkt Google Business?',
      slug: 'hoe-werkt-google-business'
    }, {
      title: 'Wat is Apple Business Connect?',
      slug: 'wat-is-apple-business-connect'
    }, {
      title: 'Hoe word je lokaal vindbaar in jouw regio?',
      slug: 'lokaal-vindbaar-worden'
    }]
  }, {
    title: 'Authority & Backlinks',
    articles: [{
      title: 'Wat zijn backlinks?',
      slug: 'wat-zijn-backlinks'
    }, {
      title: 'Hoe bouw je autoriteit op?',
      slug: 'hoe-bouw-je-autoriteit-op'
    }, {
      title: 'Wat is het verschil tussen goede en slechte links?',
      slug: 'goede-vs-slechte-links'
    }]
  }, {
    title: 'Programmatic SEO',
    articles: [{
      title: 'Wat is programmatic SEO?',
      slug: 'wat-is-programmatic-seo'
    }, {
      title: 'Hoe schaal je naar 100+ pagina’s?',
      slug: 'hoe-schaal-je-paginas'
    }, {
      title: 'Hoe voorkom je duplicate content?',
      slug: 'voorkom-duplicate-content'
    }]
  }, {
    title: 'AI & Entity SEO',
    articles: [{
      title: 'Wat is entity SEO?',
      slug: 'wat-is-entity-seo'
    }, {
      title: 'Hoe werkt SEO voor AI tools zoals ChatGPT?',
      slug: 'seo-voor-ai-tools'
    }, {
      title: 'Wat is structured data?',
      slug: 'wat-is-structured-data'
    }]
  }, {
    title: 'UX & Conversie',
    articles: [{
      title: 'Wat is UX en waarom beïnvloedt het SEO?',
      slug: 'wat-is-ux-en-waarom-beinvloedt-het-seo'
    }, {
      title: 'Hoe verhoog je conversie op je website?',
      slug: 'hoe-verhoog-je-conversie'
    }, {
      title: 'Waarom verlaten bezoekers je website?',
      slug: 'waarom-verlaten-bezoekers-website'
    }]
  }];
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-1639">
      <SEO title="SEO financieel adviseur | Noord Digital" description="SEO is geen losse dienst, het is een systeem. Ontdek hoe wij met een sterke silo-structuur zorgen voor structurele online groei voor financieel adviseurs." keywords="SEO financieel adviseur, SEO systeem, SEO silo, zoekmachine optimalisatie financiële dienstverlening" canonical="https://noorddigital.nl/seo-financieel-adviseur" data-id="element-1640" />
      <Header data-id="element-1641" />

      {/* 1. HERO SECTION */}
      <section className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] py-20 md:py-28 relative overflow-hidden" data-id="element-1642">
        <div className="absolute inset-0 opacity-10" data-id="element-1643">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80')] bg-cover bg-center" data-id="element-1644"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10" data-id="element-1645">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-1646">
            <div className="lg:col-span-7" data-id="element-1647">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20 mb-8" data-id="element-1648">
                <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-8 h-8 rounded-full border border-white/30" data-id="element-1649" />
                <span className="text-white/90 text-sm font-medium" data-id="element-1650">
                  Norden helpt je door de wirwar van SEO heen
                </span>
              </motion.div>

              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-1651">
                SEO dat werkt als systeem, niet als trucje
              </motion.h1>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="text-white/90 mb-10 text-xl leading-relaxed font-medium max-w-2xl" data-id="element-1652">
                De meeste websites zijn onzichtbaar omdat de structuur
                ontbreekt. Wij bouwen geen losse blogs, we bouwen een schaalbaar
                systeem voor structurele groei.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="flex flex-wrap gap-4" data-id="element-1653">
                <a href="/prijsscan" className="inline-flex items-center px-8 py-4 bg-white text-[#007AFF] font-bold rounded-lg hover:bg-gray-50 transition duration-300 shadow-lg group" data-id="element-1654">
                  Start de scan
                  <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1655" />
                </a>
                <a href="/contact" className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-white/30 text-white font-bold rounded-lg hover:bg-white/10 transition duration-300" data-id="element-1656">
                  Plan een gesprek
                </a>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHAT SEO REALLY IS */}
      <section className="py-20 bg-white" data-id="element-1657">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1658">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1659">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6" data-id="element-1660">
              De harde waarheid over SEO
            </h2>
            <p className="text-lg text-[#6B7280] leading-relaxed" data-id="element-1661">
              SEO is geen losse dienst die je even "aanzet". Het is een
              vermenigvuldiging van factoren. Als één van deze factoren een nul
              is, is het eindresultaat ook nul.
            </p>
          </div>

          <div className="bg-[#F7F8FA] rounded-2xl p-8 md:p-12 border border-gray-100 mb-12" data-id="element-1662">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-xl md:text-2xl font-bold text-[#1B2B44] text-center flex-wrap" data-id="element-1663">
              <span data-id="element-1664">Zichtbaarheid</span>
              <span className="text-[#007AFF]" data-id="element-1665">=</span>
              <span className="flex items-center gap-2" data-id="element-1666">
                <WrenchIcon className="w-6 h-6 text-[#007AFF]" data-id="element-1667" /> Techniek
              </span>
              <span className="text-[#007AFF]" data-id="element-1668">×</span>
              <span className="flex items-center gap-2" data-id="element-1669">
                <FileTextIcon className="w-6 h-6 text-[#007AFF]" data-id="element-1670" /> Content
              </span>
              <span className="text-[#007AFF]" data-id="element-1671">×</span>
              <span className="flex items-center gap-2" data-id="element-1672">
                <ShieldCheckIcon className="w-6 h-6 text-[#007AFF]" data-id="element-1673" />{' '}
                Autoriteit
              </span>
              <span className="text-[#007AFF]" data-id="element-1674">×</span>
              <span className="flex items-center gap-2" data-id="element-1675">
                <MousePointerClickIcon className="w-6 h-6 text-[#007AFF]" data-id="element-1676" /> UX
              </span>
              <span className="text-[#007AFF]" data-id="element-1677">×</span>
              <span className="flex items-center gap-2" data-id="element-1678">
                <Share2Icon className="w-6 h-6 text-[#007AFF]" data-id="element-1679" /> Distributie
              </span>
            </div>
            <div className="mt-8 flex items-center justify-center gap-3" data-id="element-1680">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full border border-gray-300" data-id="element-1681" />
              <p className="text-sm italic text-[#6B7280]" data-id="element-1682">
                Norden legt het simpel uit: als één factor ontbreekt, valt het
                hele systeem.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6" data-id="element-1683">
            {seoFactors.map((factor, idx) => <div key={idx} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-1684">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-[#007AFF] mb-4" data-id="element-1685">
                  {factor.icon}
                </div>
                <h3 className="font-bold text-[#111827] mb-2" data-id="element-1686">
                  {factor.title}
                </h3>
                <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-1687">
                  {factor.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 3. SEO MATURITY MODEL */}
      <section className="py-20 bg-[#F7F8FA]" data-id="element-1688">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1689">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1690">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6" data-id="element-1691">
              Het SEO Volwassenheidsmodel
            </h2>
            <p className="text-lg text-[#6B7280] leading-relaxed" data-id="element-1692">
              Groei in SEO gaat in fases. Je kunt geen dominantie verwachten als
              de fundering nog ontbreekt. Waar staat jouw kantoor?
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1693">
            {maturityPhases.map((phase, idx) => <div key={idx} className={`rounded-2xl p-8 border-2 relative overflow-hidden ${phase.color}`} data-id="element-1694">
                <div className="absolute top-0 right-0 p-4 opacity-20 font-black text-6xl" data-id="element-1695">
                  {phase.levels}
                </div>
                <div className="relative z-10" data-id="element-1696">
                  <span className="uppercase tracking-wider text-xs font-bold mb-2 block opacity-80" data-id="element-1697">
                    Fase {idx + 1}: {phase.phase}
                  </span>
                  <h3 className="text-2xl font-bold mb-6" data-id="element-1698">{phase.title}</h3>

                  <div className="space-y-6" data-id="element-1699">
                    <div data-id="element-1700">
                      <h4 className="font-semibold mb-2 flex items-center gap-2 opacity-90" data-id="element-1701">
                        <CheckCircleIcon className="w-4 h-4" data-id="element-1702" /> Wat gebeurt er
                        nu?
                      </h4>
                      <p className="text-sm opacity-80 leading-relaxed" data-id="element-1703">
                        {phase.happening}
                      </p>
                    </div>
                    <div data-id="element-1704">
                      <h4 className="font-semibold mb-2 flex items-center gap-2 opacity-90" data-id="element-1705">
                        <ZapIcon className="w-4 h-4" data-id="element-1706" /> Wat ontbreekt er?
                      </h4>
                      <p className="text-sm opacity-80 leading-relaxed" data-id="element-1707">
                        {phase.missing}
                      </p>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 3.5 WAAROM SEO VAAK NIET WERKT */}
      <section className="py-24 bg-white" data-id="element-1708">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1709">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1710">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6" data-id="element-1711">
              Waarom SEO bij 90% van de bedrijven niet werkt
            </h2>
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-1712">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full border border-gray-300" data-id="element-1713" />
              <p className="text-sm italic text-[#6B7280]" data-id="element-1714">
                Norden ziet het keer op keer: bedrijven investeren in SEO, maar
                missen de structuur om het te laten werken.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-1715">
            <div className="bg-[#F7F8FA] p-6 rounded-xl border border-gray-100" data-id="element-1716">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500 mb-4" data-id="element-1717">
                <AlertTriangleIcon className="w-5 h-5" data-id="element-1718" />
              </div>
              <h3 className="font-bold text-[#111827] mb-2" data-id="element-1719">
                1. Losse blogs zonder structuur
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-1720">
                Willekeurige artikelen schrijven zonder dat ze onderling
                verbonden zijn. Google snapt de context niet.
              </p>
            </div>
            <div className="bg-[#F7F8FA] p-6 rounded-xl border border-gray-100" data-id="element-1721">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500 mb-4" data-id="element-1722">
                <TargetIcon className="w-5 h-5" data-id="element-1723" />
              </div>
              <h3 className="font-bold text-[#111827] mb-2" data-id="element-1724">
                2. Verkeerde zoekintentie
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-1725">
                Proberen te ranken met een verkooppagina op een zoekterm waarbij
                de gebruiker eigenlijk informatie zoekt.
              </p>
            </div>
            <div className="bg-[#F7F8FA] p-6 rounded-xl border border-gray-100" data-id="element-1726">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500 mb-4" data-id="element-1727">
                <RefreshCwIcon className="w-5 h-5" data-id="element-1728" />
              </div>
              <h3 className="font-bold text-[#111827] mb-2" data-id="element-1729">
                3. Te snel opgeven
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-1730">
                Stoppen na 3 maanden omdat er nog geen leads zijn, precies
                wanneer het vliegwiel op gang begint te komen.
              </p>
            </div>
            <div className="bg-[#F7F8FA] p-6 rounded-xl border border-gray-100" data-id="element-1731">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500 mb-4" data-id="element-1732">
                <CodeIcon className="w-5 h-5" data-id="element-1733" />
              </div>
              <h3 className="font-bold text-[#111827] mb-2" data-id="element-1734">
                4. Techniek zonder content
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-1735">
                Een razendsnelle website hebben, maar geen antwoord geven op de
                vragen van je doelgroep.
              </p>
            </div>
            <div className="bg-[#F7F8FA] p-6 rounded-xl border border-gray-100" data-id="element-1736">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500 mb-4" data-id="element-1737">
                <NetworkIcon className="w-5 h-5" data-id="element-1738" />
              </div>
              <h3 className="font-bold text-[#111827] mb-2" data-id="element-1739">
                5. Geen interne linking
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-1740">
                Pagina's die nergens naar verwijzen. De SEO-waarde stroomt niet
                door de website heen.
              </p>
            </div>
          </div>

          <div className="text-center" data-id="element-1741">
            <a href="/seo/blog/waarom-werkt-seo-vaak-niet" className="inline-flex items-center text-[#007AFF] font-semibold hover:text-[#005FCC] transition-colors group" data-id="element-1742">
              Lees de uitgebreide analyse waarom SEO vaak faalt
              <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1743" />
            </a>
          </div>
        </div>
      </section>

      {/* 4. SEO SILO (MOST IMPORTANT) */}
      <section className="py-32 bg-[#1B2B44] text-white relative overflow-hidden" data-id="element-1744">
        {/* Decorative background lines */}
        <div className="absolute inset-0 opacity-5" data-id="element-1745">
          <div className="absolute top-0 left-1/4 w-px h-full bg-white" data-id="element-1746"></div>
          <div className="absolute top-0 left-2/4 w-px h-full bg-white" data-id="element-1747"></div>
          <div className="absolute top-0 left-3/4 w-px h-full bg-white" data-id="element-1748"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10" data-id="element-1749">
          <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-1750">
            <div className="inline-flex items-center gap-3 bg-white/10 px-4 py-2 rounded-full border border-white/20 mb-6" data-id="element-1751">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full" data-id="element-1752" />
              <span className="text-white text-sm font-bold tracking-wide uppercase" data-id="element-1753">
                Onze kernstrategie
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight" data-id="element-1754">
              De SEO Silo: Waarom structuur alles bepaalt
            </h2>

            <p className="text-xl text-white/80 leading-relaxed mb-8" data-id="element-1755">
              Wij bouwen een <strong data-id="element-1756">SEO Silo structuur</strong>. We groeperen
              gerelateerde content in strakke hiërarchieën (Home → Categorie →
              Detail). Door slimme interne linking vloeit de SEO-waarde door je
              hele site. Dit is het geheim achter schaalbare groei en ware
              autoriteit.
            </p>

            <div className="flex items-center justify-center gap-3 mb-8" data-id="element-1757">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-8 h-8 rounded-full border-2 border-white/30" data-id="element-1758" />
              <p className="text-base italic text-white/70" data-id="element-1759">
                "Norden helpt hier structuur aan te brengen. Dit is waar de
                meeste websites falen, en waar wij het verschil maken."
              </p>
            </div>

            <a href="/seo/blog/seo-silo-wat-is-het" className="inline-flex items-center bg-[#007AFF] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#005FCC] transition-colors group" data-id="element-1760">
              Lees alles over de SEO Silo structuur
              <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1761" />
            </a>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20" data-id="element-1762">
            {/* Zonder Silo */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8" data-id="element-1763">
              <h3 className="text-2xl font-bold mb-6 text-red-400 flex items-center gap-3" data-id="element-1764">
                <AlertTriangleIcon className="w-6 h-6" data-id="element-1765" /> Zonder Silo (Chaos)
              </h3>
              <ul className="space-y-4 text-white/80" data-id="element-1766">
                <li className="flex items-start gap-3" data-id="element-1767">
                  <span className="text-red-400 mt-1" data-id="element-1768">✗</span>
                  Pagina's staan los van elkaar, Google snapt de context niet.
                </li>
                <li className="flex items-start gap-3" data-id="element-1769">
                  <span className="text-red-400 mt-1" data-id="element-1770">✗</span>
                  SEO-waarde (link juice) stroomt niet door de website.
                </li>
                <li className="flex items-start gap-3" data-id="element-1771">
                  <span className="text-red-400 mt-1" data-id="element-1772">✗</span>
                  Bezoekers verdwalen en verlaten de site snel.
                </li>
                <li className="flex items-start gap-3" data-id="element-1773">
                  <span className="text-red-400 mt-1" data-id="element-1774">✗</span>
                  Je concurreert met je eigen pagina's (keyword
                  cannibalization).
                </li>
              </ul>
            </div>

            {/* Met Silo */}
            <div className="bg-gradient-to-br from-[#007AFF]/20 to-transparent border border-[#007AFF]/30 rounded-2xl p-8" data-id="element-1775">
              <h3 className="text-2xl font-bold mb-6 text-[#007AFF] flex items-center gap-3" data-id="element-1776">
                <CheckCircleIcon className="w-6 h-6" data-id="element-1777" /> Met Silo (Groei)
              </h3>
              <ul className="space-y-4 text-white/90" data-id="element-1778">
                <li className="flex items-start gap-3" data-id="element-1779">
                  <span className="text-[#007AFF] mt-1" data-id="element-1780">✓</span>
                  Strakke hiërarchie: Google ziet jou als dé autoriteit.
                </li>
                <li className="flex items-start gap-3" data-id="element-1781">
                  <span className="text-[#007AFF] mt-1" data-id="element-1782">✓</span>
                  Eén sterke pagina trekt de hele cluster mee omhoog.
                </li>
                <li className="flex items-start gap-3" data-id="element-1783">
                  <span className="text-[#007AFF] mt-1" data-id="element-1784">✓</span>
                  Logische navigatie houdt bezoekers langer vast.
                </li>
                <li className="flex items-start gap-3" data-id="element-1785">
                  <span className="text-[#007AFF] mt-1" data-id="element-1786">✓</span>
                  Schaalbaar: nieuwe content voegt direct waarde toe.
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white/5 rounded-3xl p-4 sm:p-6 md:p-10 border border-white/10 max-w-4xl mx-auto" data-id="element-1787">
            <div className="flex flex-col items-center" data-id="element-1788">
              {/* Home */}
              <div className="bg-[#007AFF] text-white px-6 sm:px-10 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-xl mb-8 relative z-10 shadow-lg border border-white/20" data-id="element-1789">
                Home
              </div>

              {/* Lines from Home to Categories */}
              <div className="w-full max-w-2xl h-px bg-white/20 relative -mt-4 mb-8 z-0" data-id="element-1790">
                <div className="absolute left-1/2 top-0 w-px h-8 bg-white/20 -translate-y-full" data-id="element-1791"></div>
                <div className="absolute left-0 top-0 w-px h-8 bg-white/20" data-id="element-1792"></div>
                <div className="absolute left-1/2 top-0 w-px h-8 bg-white/20" data-id="element-1793"></div>
                <div className="absolute right-0 top-0 w-px h-8 bg-white/20" data-id="element-1794"></div>
              </div>

              {/* Categories */}
              <div className="w-full max-w-3xl flex justify-between px-0 sm:px-2 md:px-4 mb-12 relative z-10" data-id="element-1795">
                <div className="bg-white text-[#1B2B44] px-2 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 rounded-lg font-bold text-xs sm:text-sm md:text-base shadow-md" data-id="element-1796">
                  Hypotheken
                </div>
                <div className="bg-white text-[#1B2B44] px-2 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 rounded-lg font-bold text-xs sm:text-sm md:text-base shadow-md" data-id="element-1797">
                  Pensioenen
                </div>
                <div className="bg-white text-[#1B2B44] px-2 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 rounded-lg font-bold text-xs sm:text-sm md:text-base shadow-md" data-id="element-1798">
                  Verzekeringen
                </div>
              </div>

              {/* Lines from Categories to Details */}
              <div className="w-full max-w-3xl flex justify-between px-3 sm:px-6 md:px-10 relative -mt-6 mb-6 z-0" data-id="element-1799">
                <div className="w-px h-6 bg-white/20" data-id="element-1800"></div>
                <div className="w-px h-6 bg-white/20" data-id="element-1801"></div>
                <div className="w-px h-6 bg-white/20" data-id="element-1802"></div>
              </div>

              {/* Detail pages (Clusters) */}
              <div className="w-full max-w-3xl flex justify-between px-0 sm:px-2" data-id="element-1803">
                <div className="flex flex-col gap-3" data-id="element-1804">
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-1805">
                    Oversluiten
                  </div>
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-1806">
                    Starters
                  </div>
                </div>
                <div className="flex flex-col gap-3" data-id="element-1807">
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-1808">
                    Lijfrente
                  </div>
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-1809">
                    ZZP
                  </div>
                </div>
                <div className="flex flex-col gap-3" data-id="element-1810">
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-1811">
                    AOV
                  </div>
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-1812">
                    Zakelijk
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20 border-t border-white/10 pt-12" data-id="element-1813">
            <div className="text-center" data-id="element-1814">
              <div className="text-4xl font-black text-[#007AFF] mb-2" data-id="element-1815">3x</div>
              <p className="text-white/80 font-medium" data-id="element-1816">
                Meer organisch verkeer
              </p>
            </div>
            <div className="text-center" data-id="element-1817">
              <div className="text-4xl font-black text-[#007AFF] mb-2" data-id="element-1818">Top</div>
              <p className="text-white/80 font-medium" data-id="element-1819">
                Hogere autoriteit per pagina
              </p>
            </div>
            <div className="text-center" data-id="element-1820">
              <div className="text-4xl font-black text-[#007AFF] mb-2" data-id="element-1821">∞</div>
              <p className="text-white/80 font-medium" data-id="element-1822">Schaalbare groei</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. OVERVIEW OF SEO COMPONENTS */}
      <section className="py-20 bg-[#F7F8FA]" data-id="element-1823">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1824">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1825">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6" data-id="element-1826">
              De componenten van ons systeem
            </h2>
            <p className="text-lg text-[#6B7280] leading-relaxed mb-6" data-id="element-1827">
              Niet alles is altijd nodig. Het hangt af van waar je nu staat in
              je SEO-reis. Hieronder vind je alle onderdelen van ons systeem.
              Klik op een component om te zien wat het inhoudt en waarom het
              belangrijk is.
            </p>
            <div className="flex items-center justify-center gap-3" data-id="element-1828">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full border border-gray-300" data-id="element-1829" />
              <p className="text-sm italic text-[#6B7280]" data-id="element-1830">
                Norden helpt je bepalen welke componenten prioriteit hebben.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1831">
            {seoComponents.map(comp => <div key={comp.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-1832">
                <button onClick={() => setExpandedComponent(expandedComponent === comp.id ? null : comp.id)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-1833">
                  <div className="flex items-center gap-4" data-id="element-1834">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-[#007AFF]" data-id="element-1835">
                      {comp.icon}
                    </div>
                    <h3 className="font-bold text-[#111827] text-lg" data-id="element-1836">
                      {comp.title}
                    </h3>
                  </div>
                  {expandedComponent === comp.id ? <ChevronUpIcon className="w-5 h-5 text-gray-400" data-id="element-1837" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-1838" />}
                </button>

                <AnimatePresence data-id="element-1839">
                  {expandedComponent === comp.id && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-1840">
                      <div className="px-6 pb-6 pt-2 border-t border-gray-50" data-id="element-1841">
                        <p className="text-[#111827] font-medium mb-4" data-id="element-1842">
                          {comp.explanation}
                        </p>

                        <div className="space-y-4 mb-6" data-id="element-1843">
                          <div data-id="element-1844">
                            <span className="text-xs font-bold uppercase tracking-wider text-[#007AFF] block mb-1" data-id="element-1845">
                              Wat we doen
                            </span>
                            <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-1846">
                              {comp.happens}
                            </p>
                          </div>
                          <div data-id="element-1847">
                            <span className="text-xs font-bold uppercase tracking-wider text-[#007AFF] block mb-1" data-id="element-1848">
                              Waarom het werkt
                            </span>
                            <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-1849">
                              {comp.matters}
                            </p>
                          </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-1850">
                          <p className="text-sm text-gray-600 mb-2" data-id="element-1851">
                            Wil je hier dieper in duiken?
                          </p>
                          <a href={comp.blogLink} className="text-sm font-semibold text-[#007AFF] hover:underline flex items-center" data-id="element-1852">
                            Lees: {comp.blogTitle}{' '}
                            <ArrowRightIcon className="w-3 h-3 ml-1" data-id="element-1853" />
                          </a>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. PROGRAMMATIC SEO & 7. SEARCH EVERYWHERE */}
      <section className="py-20 bg-white" data-id="element-1854">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1855">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-1856">
            {/* Programmatic SEO */}
            <div className="bg-[#1B2B44] rounded-2xl p-8 md:p-10 text-white relative overflow-hidden" data-id="element-1857">
              <div className="absolute top-0 right-0 opacity-10 transform translate-x-1/4 -translate-y-1/4" data-id="element-1858">
                <LayersIcon className="w-64 h-64" data-id="element-1859" />
              </div>
              <div className="relative z-10" data-id="element-1860">
                <h3 className="text-2xl font-bold mb-4 flex items-center gap-3" data-id="element-1861">
                  <DatabaseIcon className="w-6 h-6 text-[#007AFF]" data-id="element-1862" />
                  Programmatic SEO & Schalen
                </h3>
                <p className="text-white/80 leading-relaxed mb-6" data-id="element-1863">
                  Zodra de basis staat, is het tijd om te schalen. Met
                  programmatic SEO genereren we op een slimme, kwalitatieve
                  manier tientallen tot honderden geoptimaliseerde pagina's.
                </p>
                <ul className="space-y-3 mb-8" data-id="element-1864">
                  <li className="flex items-start gap-3" data-id="element-1865">
                    <CheckCircleIcon className="w-5 h-5 text-[#007AFF] flex-shrink-0 mt-0.5" data-id="element-1866" />
                    <span className="text-white/90" data-id="element-1867">
                      <strong data-id="element-1868">City pages:</strong> "Hypotheekadviseur [Stad]"
                      voor regionale dominantie.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-1869">
                    <CheckCircleIcon className="w-5 h-5 text-[#007AFF] flex-shrink-0 mt-0.5" data-id="element-1870" />
                    <span className="text-white/90" data-id="element-1871">
                      <strong data-id="element-1872">Niche pages:</strong> Specifieke oplossingen voor
                      specifieke doelgroepen.
                    </span>
                  </li>
                </ul>
                <a href="/seo/blog/wat-is-programmatic-seo" className="inline-flex items-center text-white font-semibold hover:text-[#007AFF] transition-colors group" data-id="element-1873">
                  Lees hoe we schalen naar 100+ pagina's
                  <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1874" />
                </a>
              </div>
            </div>

            {/* Search Everywhere */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 md:p-10 border border-blue-100" data-id="element-1875">
              <h3 className="text-2xl font-bold text-[#111827] mb-4 flex items-center gap-3" data-id="element-1876">
                <GlobeIcon className="w-6 h-6 text-[#007AFF]" data-id="element-1877" />
                Search is overal
              </h3>
              <p className="text-[#6B7280] leading-relaxed mb-6" data-id="element-1878">
                Mensen zoeken allang niet meer alleen via Google. Een modern SEO
                systeem zorgt dat je overal vindbaar bent waar jouw doelgroep
                zoekt.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-1879">
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3" data-id="element-1880">
                  <SearchIcon className="w-5 h-5 text-gray-400" data-id="element-1881" />
                  <span className="font-semibold text-gray-800" data-id="element-1882">Google</span>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3" data-id="element-1883">
                  <NetworkIcon className="w-5 h-5 text-blue-600" data-id="element-1884" />
                  <span className="font-semibold text-gray-800" data-id="element-1885">LinkedIn</span>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3" data-id="element-1886">
                  <PlayCircleIcon className="w-5 h-5 text-red-500" data-id="element-1887" />
                  <span className="font-semibold text-gray-800" data-id="element-1888">YouTube</span>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3" data-id="element-1889">
                  <BrainIcon className="w-5 h-5 text-purple-500" data-id="element-1890" />
                  <span className="font-semibold text-gray-800" data-id="element-1891">AI Tools</span>
                </div>
              </div>
              <a href="/seo/blog/seo-voor-ai-tools" className="inline-flex items-center text-[#007AFF] font-semibold hover:text-[#005FCC] transition-colors group" data-id="element-1892">
                Lees meer over SEO voor AI tools
                <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1893" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 8. VISUAL FORMULAS */}
      <section className="py-20 bg-[#111827] text-white" data-id="element-1894">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1895">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1896">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1897">
              De wiskunde achter online succes
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed mb-6" data-id="element-1898">
              SEO is geen magie. Het is een logisch gevolg van de juiste
              variabelen.
            </p>
            <div className="flex items-center justify-center gap-3" data-id="element-1899">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full border border-gray-600" data-id="element-1900" />
              <p className="text-sm italic text-gray-400" data-id="element-1901">
                Norden rekent het voor je uit.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4 max-w-4xl mx-auto" data-id="element-1902">
            {formulas.map((item, idx) => <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 hover:bg-white/10 transition-colors" data-id="element-1903">
                <span className="text-gray-400 font-medium text-sm uppercase tracking-wider" data-id="element-1904">
                  {item.title}
                </span>
                <span className="text-xl md:text-2xl font-bold text-white font-mono tracking-tight" data-id="element-1905">
                  {item.formula}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* 9. FAQ SECTION */}
      <section className="py-20 bg-[#F7F8FA]" data-id="element-1906">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[800px]" data-id="element-1907">
          <div className="text-center mb-12" data-id="element-1908">
            <h2 className="text-3xl font-bold text-[#111827] mb-4" data-id="element-1909">
              Veelgestelde vragen
            </h2>
            <p className="text-[#6B7280] mb-6" data-id="element-1910">
              Eerlijke antwoorden op de meest gestelde vragen over SEO.
            </p>
            <div className="flex items-center justify-center gap-3" data-id="element-1911">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full border border-gray-300" data-id="element-1912" />
              <p className="text-sm italic text-[#6B7280]" data-id="element-1913">
                De vragen die Norden het vaakst hoort van financieel adviseurs.
              </p>
            </div>
          </div>

          <div className="space-y-4" data-id="element-1914">
            {faqs.map((faq, idx) => <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1915">
                <button onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-1916">
                  <span className="font-bold text-[#111827] pr-8" data-id="element-1917">{faq.q}</span>
                  {expandedFaq === idx ? <ChevronUpIcon className="w-5 h-5 text-[#007AFF] flex-shrink-0" data-id="element-1918" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-1919" />}
                </button>
                <AnimatePresence data-id="element-1920">
                  {expandedFaq === idx && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-1921">
                      <div className="px-6 pb-6 pt-2 text-[#6B7280] leading-relaxed border-t border-gray-50" data-id="element-1922">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* 9.5 BRIDGE SECTION */}
      <section className="py-24 bg-gradient-to-r from-[#007AFF] to-[#005FCC] text-white" data-id="element-1923">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[800px] text-center" data-id="element-1924">
          <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-16 h-16 rounded-full border-2 border-white/30 mx-auto mb-6" data-id="element-1925" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1926">
            De vraag is niet óf je SEO nodig hebt, maar waar je nu staat.
          </h2>
          <p className="text-xl text-white/80 mb-10" data-id="element-1927">
            Doe de scan en ontdek in 2 minuten waar jouw grootste groeikansen
            liggen.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1928">
            <a href="/prijsscan" className="inline-flex justify-center items-center px-8 py-4 bg-white text-[#007AFF] font-bold rounded-lg hover:bg-gray-50 transition duration-300 shadow-lg group" data-id="element-1929">
              Start de SEO scan
              <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1930" />
            </a>
            <a href="/contact" className="inline-flex justify-center items-center px-8 py-4 bg-transparent border-2 border-white/30 text-white font-bold rounded-lg hover:bg-white/10 transition duration-300" data-id="element-1931">
              Plan een strategiegesprek
            </a>
          </div>
        </div>
      </section>

      {/* 10. BLOG ARTICLES OVERVIEW */}
      <section className="py-24 bg-white" data-id="element-1932">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1933">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1934">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6" data-id="element-1935">
              Duik dieper in de materie
            </h2>
            <p className="text-lg text-[#6B7280] leading-relaxed" data-id="element-1936">
              Onze complete kennisbank. Geen oppervlakkige tips, maar diepgaande
              strategische artikelen over hoe SEO écht werkt.
            </p>
          </div>

          {/* Featured Article */}
          <div className="mb-16 bg-[#F7F8FA] rounded-3xl p-8 md:p-12 border border-gray-200" data-id="element-1937">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center" data-id="element-1938">
              <div data-id="element-1939">
                <span className="inline-block px-3 py-1 bg-blue-100 text-[#007AFF] text-sm font-bold rounded-full mb-4 uppercase tracking-wider" data-id="element-1940">
                  Cornerstone Artikel
                </span>
                <h3 className="text-3xl font-bold text-[#111827] mb-4" data-id="element-1941">
                  Wat is een SEO silo en hoe werkt het?
                </h3>
                <p className="text-[#6B7280] text-lg leading-relaxed mb-8" data-id="element-1942">
                  Dit is het belangrijkste artikel in onze kennisbank. Ontdek
                  waarom 90% van de websites faalt in Google en hoe je met een
                  strakke silo-structuur de concurrentie domineert.
                </p>
                <a href="/seo/blog/seo-silo-wat-is-het" className="inline-flex items-center bg-[#1B2B44] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#111827] transition-colors group" data-id="element-1943">
                  Lees het cornerstone artikel
                  <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1944" />
                </a>
              </div>
              <div className="relative hidden lg:block" data-id="element-1945">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#007AFF]/20 to-transparent rounded-2xl transform rotate-3" data-id="element-1946"></div>
                <div className="bg-white p-8 rounded-2xl border border-gray-200 relative z-10 shadow-lg" data-id="element-1947">
                  <div className="w-full h-40 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center mb-6" data-id="element-1948">
                    <NetworkIcon className="w-12 h-12 text-gray-300" data-id="element-1949" />
                  </div>
                  <div className="h-4 bg-gray-100 rounded w-3/4 mb-3" data-id="element-1950"></div>
                  <div className="h-4 bg-gray-100 rounded w-1/2" data-id="element-1951"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Clusters Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1952">
            {blogClusters.map((cluster, idx) => <div key={idx} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1953">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100" data-id="element-1954">
                  <h3 className="text-lg font-bold text-[#1B2B44] flex items-center gap-2" data-id="element-1955">
                    <span className="w-6 h-6 rounded-full bg-blue-50 text-[#007AFF] flex items-center justify-center text-xs" data-id="element-1956">
                      {idx + 1}
                    </span>
                    {cluster.title}
                  </h3>
                  <span className="text-xs font-semibold text-gray-400 bg-gray-50 px-2 py-1 rounded-md" data-id="element-1957">
                    {cluster.articles.length} artikelen
                  </span>
                </div>
                <ul className="space-y-3" data-id="element-1958">
                  {cluster.articles.map((article, aIdx) => <li key={aIdx} data-id="element-1959">
                      <a href={`/seo/blog/${article.slug}`} className="text-sm text-[#4B5563] hover:text-[#007AFF] hover:underline flex items-start gap-2 leading-snug group" data-id="element-1960">
                        <ChevronRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-300 group-hover:text-[#007AFF] transition-colors" data-id="element-1961" />
                        {article.title}
                      </a>
                    </li>)}
                </ul>
              </div>)}
          </div>

          {/* Meer artikelen CTA */}
          <div className="mt-16 text-center" data-id="element-1962">
            <a href="/bibliotheek" className="inline-flex items-center bg-white border-2 border-[#1B2B44] text-[#1B2B44] hover:bg-[#1B2B44] hover:text-white font-bold py-4 px-8 rounded-lg transition duration-300 shadow-sm hover:shadow-md group" data-id="element-1963">
              Bekijk alle artikelen in de Bibliotheek
              <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1964" />
            </a>
          </div>
        </div>
      </section>

      {/* 11. FINAL CTA */}
      <section className="py-24 bg-gradient-to-br from-[#007AFF] to-[#1B2B44] relative overflow-hidden" data-id="element-1965">
        <div className="absolute inset-0 opacity-10" data-id="element-1966">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80')] bg-cover bg-center" data-id="element-1967"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[800px] relative z-10 text-center" data-id="element-1968">
          <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden mascotte" className="w-24 h-24 rounded-full border-4 border-white/20 mx-auto mb-8 shadow-xl" data-id="element-1969" />

          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-1970">
            SEO is geen losse dienst. Het is een systeem dat moet kloppen.
          </h2>

          <p className="text-xl text-white/80 mb-10 leading-relaxed" data-id="element-1971">
            Klaar om te stoppen met losse flodders en te starten met structurele
            groei? Laten we kijken waar jouw kansen liggen.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1972">
            <a href="/prijsscan" className="inline-flex justify-center items-center px-8 py-4 bg-white text-[#007AFF] font-bold rounded-lg hover:bg-gray-50 transition duration-300 shadow-lg group" data-id="element-1973">
              Start de scan
              <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1974" />
            </a>
            <a href="/contact" className="inline-flex justify-center items-center px-8 py-4 bg-transparent border-2 border-white/30 text-white font-bold rounded-lg hover:bg-white/10 transition duration-300" data-id="element-1975">
              Plan een gesprek
            </a>
          </div>
        </div>
      </section>

      <Footer data-id="element-1976" />
    </div>;
};